import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { TenantDetail } from '@sellokart/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { SelectField, TextAreaField, TextField } from '@/components/Form';
import { IconArrowLeft } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { ErrorState, Skeleton } from '@/components/States';
import { NO_PLAN, PlanSelectField } from '@/features/plans/PlanSelectField';
import { fetchTenant, tenantKeys, updateTenant } from '@/features/tenants/api';
import type { UpdateTenantInput } from '@/features/tenants/api';
import { getErrorMessage } from '@/lib/api';
import { timezoneOptions } from '@/lib/timezones';
import { compactErrors, requiredText } from '@/lib/validate';

const CURRENCIES: readonly string[] = [
  'INR',
  'USD',
  'EUR',
  'GBP',
  'AED',
  'SAR',
  'MYR',
  'SGD',
];

interface FormState {
  name: string;
  phone: string;
  address: string;
  timezone: string;
  currency: string;
  /** NO_PLAN means no plan — the same deliberately-unassigned state the API uses. */
  planId: string;
}

type FormField = keyof FormState;

function toFormState(tenant: TenantDetail): FormState {
  return {
    name: tenant.name,
    phone: tenant.phone ?? '',
    address: tenant.address ?? '',
    timezone: tenant.timezone,
    currency: tenant.currency,
    planId: tenant.plan?.id ?? NO_PLAN,
  };
}

function BackLink({ tenantId }: { tenantId: string }) {
  return (
    <Link
      to={`/cp/tenants/${tenantId}`}
      className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
    >
      <IconArrowLeft className="h-4 w-4" />
      Tenant
    </Link>
  );
}

/**
 * A full page rather than a modal: editing a tenant is a deliberate, navigable
 * action that is deep-linkable and survives a reload, not a quick aside stacked
 * on top of the detail page.
 *
 * Deliberately carries NO logo control and NO status switch. Both live as their
 * own actions on the detail page — the logo because it applies immediately with
 * no "save", and the status because deactivating a shop signs out every one of
 * its users and deserves a confirmation step rather than being one field among
 * eight that a Save button sweeps up.
 */
export function EditTenantPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isPending, isError, error, refetch, isFetching } = useQuery({
    queryKey: tenantKeys.detail(id),
    queryFn: ({ signal }) => fetchTenant(id, signal),
    enabled: id !== '',
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Partial<Record<FormField, string>>>({});

  // Seeded once the tenant arrives. Held as null until then so a half-empty
  // form is never shown and then silently overwritten under the operator.
  useEffect(() => {
    if (data) setForm(toFormState(data));
  }, [data]);

  const mutation = useMutation({
    mutationFn: (input: UpdateTenantInput) => updateTenant(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      navigate(`/cp/tenants/${id}`);
    },
  });

  const update = useCallback((patch: Partial<FormState>) => {
    setForm((current) => (current ? { ...current, ...patch } : current));
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!form) return;

      const validation = compactErrors<FormField>({
        name: requiredText(form.name, 'Shop name'),
        timezone: requiredText(form.timezone, 'Timezone'),
        currency: requiredText(form.currency, 'Currency'),
      });
      setErrors(validation);
      if (Object.keys(validation).length > 0) return;

      mutation.mutate({
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        timezone: form.timezone.trim(),
        currency: form.currency.trim().toUpperCase(),
        // An explicit null is how a tenant is taken OFF a plan. Sending
        // undefined would silently leave the old plan in place.
        planId: form.planId === NO_PLAN ? null : form.planId,
      });
    },
    [form, mutation, id],
  );

  if (isPending || form === null) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-56" />
        <div className="card space-y-4 p-5">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-2/3" />
        </div>
      </div>
    );
  }

  if (isError || data === undefined) {
    return (
      <ErrorState
        error={error}
        title="Could not load this tenant"
        onRetry={() => void refetch()}
        retrying={isFetching}
      />
    );
  }

  const currencyOptions = CURRENCIES.includes(form.currency)
    ? CURRENCIES
    : [form.currency, ...CURRENCIES];

  return (
    <>
      <BackLink tenantId={id} />
      <PageHeader
        title={`Edit ${data.name}`}
        description="Timezone and currency are how this shop's POS dates and money are interpreted."
      />

      <form onSubmit={handleSubmit} noValidate className="max-w-3xl space-y-6">
        <section className="card p-5">
          {mutation.isError ? (
            <Alert tone="error" className="mb-5">
              {getErrorMessage(mutation.error)}
            </Alert>
          ) : null}

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <TextField
                id="name"
                label="Shop name"
                required
                value={form.name}
                onChange={(event) => update({ name: event.target.value })}
                error={errors.name}
                disabled={mutation.isPending}
              />
            </div>

            <TextField
              id="phone"
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(event) => update({ phone: event.target.value })}
              disabled={mutation.isPending}
            />

            <SelectField
              id="timezone"
              label="Timezone"
              required
              value={form.timezone}
              onChange={(event) => update({ timezone: event.target.value })}
              error={errors.timezone}
              disabled={mutation.isPending}
            >
              {timezoneOptions(form.timezone).map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </SelectField>

            <SelectField
              id="currency"
              label="Currency"
              required
              value={form.currency}
              onChange={(event) => update({ currency: event.target.value })}
              error={errors.currency}
              disabled={mutation.isPending}
            >
              {currencyOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </SelectField>

            <PlanSelectField
              value={form.planId}
              onChange={(planId) => update({ planId })}
            />

            <div className="sm:col-span-2">
              <TextAreaField
                id="address"
                label="Address"
                value={form.address}
                onChange={(event) => update({ address: event.target.value })}
                disabled={mutation.isPending}
              />
            </div>
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            onClick={() => navigate(`/cp/tenants/${id}`)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Save changes
          </Button>
        </div>
      </form>
    </>
  );
}
